<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0

@component
Displays the transcript of a file including the translation and a form to edit the speakers. Needs a SingleTranscript instance in the property "transcript"
-->

<script>
import { tick } from 'svelte';
import Link from '../../components/Link.svelte';
import { getApiUrl } from '../../config.js';
import { SingleTranscript } from '../../libs/TranscriptionAPI/TranscriptionJobs.js';
import { TranscriptionJobsStore } from '../../libs/TranscriptionAPI/TranscriptionJobsStore.js';
import { del, get, put } from '../../utils/CoreService.js';
import { getMicroserviceConfig } from '../../utils/MicroserviceConfigs';
import uiStore from '../../utils/UIStore';
import { copyWithDisclaimers } from '../../utils/copyHook.js';
import TranscriptMediaPlayer from './TranscriptMediaPlayer.svelte';

/**
 * @param - the SingleTranscript instance that should be rendered
 */
export let transcript;
export let job;
let refreshRevision = 0;
let transcriptRows = [];
let translationRows = [];
let hasSpeakers = false;
let transcriptionDisclaimerBefore = '';
let transcriptionDisclaimerAfter = '';
let translationDisclaimerBefore = '';
let translationDisclaimerAfter = '';
let correctionStatus = 'saved';
let undoStack = [];
let dialogSpeakerId = null;
let dialogStep = 'choice';
let editName = '';
let mergeTarget = '';
let renameConflictSpeakerId = null;
let speakerNameError = '';
let editorPosition = { top: 0, left: 0 };
let editorInput;

$: {
  refreshRevision;
  transcriptRows = transcript.getTranscriptRows();
  translationRows = transcript.getTranslationRows();
  hasSpeakers = Object.keys(transcript.getSpeakerMap()).length > 0;
}

async function loadDisclaimers() {
  const [
    fetchedTranscriptionDisclaimerBefore,
    fetchedTranscriptionDisclaimerAfter,
    fetchedTranslationDisclaimerBefore,
    fetchedTranslationDisclaimerAfter,
  ] = await Promise.all([
    getMicroserviceConfig('transcription', 'transcription_disclaimer_before'),
    getMicroserviceConfig('transcription', 'transcription_disclaimer_after'),
    getMicroserviceConfig('transcription', 'translation_disclaimer_before'),
    getMicroserviceConfig('transcription', 'translation_disclaimer_after'),
  ]);

  transcriptionDisclaimerBefore = fetchedTranscriptionDisclaimerBefore ?? '';
  transcriptionDisclaimerAfter = fetchedTranscriptionDisclaimerAfter ?? '';
  translationDisclaimerBefore = fetchedTranslationDisclaimerBefore ?? '';
  translationDisclaimerAfter = fetchedTranslationDisclaimerAfter ?? '';
}

loadDisclaimers();

/**
 * Event handler: user updated the speaker names in the form.
 * @param key - The key of the speaker
 * @param newName - The name the user gave to this speaker
 */
function refreshTranscript() {
  TranscriptionJobsStore.update((jobs) => {
    return jobs.map((job) => {
      if (job.transcription === transcript) {
        job.transcription = transcript;
      }
      return job;
    });
  });
  refreshRevision += 1;
}

function cloneCorrections(corrections = transcript.getSpeakerCorrections()) {
  return JSON.parse(JSON.stringify(corrections));
}

function sleep(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

async function saveCorrections(corrections = transcript.getSpeakerCorrections()) {
  correctionStatus = 'saving';
  const url = `${getApiUrl()}/transcription/jobs/${encodeURIComponent(job.getJobId())}/speaker-corrections`;
  let lastError;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await put(url, {
        ...corrections,
        revision: transcript.getSpeakerCorrectionsRevision(),
      });
      if (response.status === 409) {
        await reloadTranscript();
        correctionStatus = 'conflict';
        return false;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const stored = await response.json();
      transcript.applySpeakerCorrections(stored.speaker_corrections);
      transcript.setSpeakerCorrectionsRevision(stored.speaker_corrections_revision);
      correctionStatus = 'saved';
      refreshTranscript();
      return true;
    } catch (error) {
      lastError = error;
      if (attempt < 2) await sleep(250 * 2 ** attempt);
    }
  }
  console.error('Could not save speaker corrections', lastError);
  correctionStatus = 'failed';
  return false;
}

async function commitCorrections(nextCorrections) {
  undoStack = [...undoStack.slice(-4), cloneCorrections()];
  transcript.applySpeakerCorrections(nextCorrections);
  refreshTranscript();
  await saveCorrections(nextCorrections);
}

async function openSpeakerDialog(speakerId, event) {
  if (correctionStatus === 'saving') return;
  const rect = event.currentTarget.getBoundingClientRect();
  const editorWidth = 340;
  editorPosition = {
    top: Math.min(rect.bottom + 8, window.innerHeight - 180),
    left: Math.max(8, Math.min(rect.left, window.innerWidth - editorWidth - 8)),
  };
  dialogSpeakerId = speakerId;
  dialogStep = 'rename';
  editName = transcript.getSpeakerLabel(speakerId);
  mergeTarget = '';
  renameConflictSpeakerId = null;
  speakerNameError = '';
  await tick();
  editorInput?.focus();
  editorInput?.select();
}

function closeSpeakerDialog() {
  dialogSpeakerId = null;
}

function validateSpeakerName(value) {
  const name = value.trim();
  if (!name) return { name, error: 'Bitte einen Namen eingeben.' };
  for (const character of name) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f) || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
      return { name, error: 'Der Name darf keine Steuerzeichen, Tabs oder Zeilenumbrüche enthalten.' };
    }
  }
  return { name, error: '' };
}

function handleSpeakerNameInput() {
  renameConflictSpeakerId = null;
  speakerNameError = validateSpeakerName(editName).error;
}

async function confirmRename() {
  if (correctionStatus === 'saving') return;
  const { name, error } = validateSpeakerName(editName);
  speakerNameError = error;
  if (error) return;
  const normalizedName = name.toLocaleLowerCase('de-DE');
  renameConflictSpeakerId =
    Object.entries(transcript.getSpeakerMap()).find(
      ([speakerId, speaker]) =>
        speakerId !== dialogSpeakerId && speaker.name.trim().toLocaleLowerCase('de-DE') === normalizedName,
    )?.[0] ?? null;
  if (renameConflictSpeakerId) return;
  const next = cloneCorrections();
  next.names[dialogSpeakerId] = name;
  closeSpeakerDialog();
  await commitCorrections(next);
}

async function confirmMerge() {
  if (correctionStatus === 'saving') return;
  if (!mergeTarget || mergeTarget === dialogSpeakerId) return;
  const next = cloneCorrections();
  for (const [source, target] of Object.entries(next.aliases)) {
    if (target === dialogSpeakerId) next.aliases[source] = mergeTarget;
  }
  next.aliases[dialogSpeakerId] = mergeTarget;
  delete next.names[dialogSpeakerId];
  closeSpeakerDialog();
  await commitCorrections(next);
}

async function undoSpeakerChange() {
  if (correctionStatus === 'saving') return;
  const previous = undoStack.at(-1);
  if (!previous) return;
  undoStack = undoStack.slice(0, -1);
  transcript.applySpeakerCorrections(previous);
  refreshTranscript();
  await saveCorrections(previous);
}

async function resetSpeakerCorrections() {
  if (correctionStatus === 'saving') return;
  if (!window.confirm('Alle Sprecherkorrekturen zurücksetzen?')) return;
  correctionStatus = 'saving';
  const url = `${getApiUrl()}/transcription/jobs/${encodeURIComponent(job.getJobId())}/speaker-corrections?revision=${encodeURIComponent(transcript.getSpeakerCorrectionsRevision())}`;
  try {
    const response = await del(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    await reloadTranscript();
    undoStack = [];
    correctionStatus = 'saved';
    refreshTranscript();
  } catch (error) {
    console.error('Could not reset speaker corrections', error);
    correctionStatus = 'failed';
  }
}

async function reloadTranscript() {
  const response = await get(
    `${getApiUrl()}/transcription/retrieve_transcription/${encodeURIComponent(job.getJobId())}`,
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = (await response.json()).transcript;
  job.setTranscription(
    new SingleTranscript(
      job.getJobId(),
      payload.result,
      null,
      payload.matched_terms ?? {},
      payload.translation ?? [],
      payload.language ?? null,
      payload.speaker_corrections ?? {},
      payload.speaker_corrections_revision ?? 0,
    ),
  );
  transcript = job.getTranscription();
  refreshTranscript();
}

function getSpeakerDisplayName(speakerId) {
  return transcript.getSpeakerLabel(speakerId);
}

function formatScore(score) {
  return Number.isFinite(score) ? Number(score).toFixed(2) : '0.00';
}

let mediaPlayerOpen = false;
let mediaTime = null;
let mediaVolume = 1;
let mediaMuted = false;
let mediaPlayerPosition = { x: 96, y: 120 };
let mediaPlayerSize = null;
let seekRequest = null;
let seekRequestId = 0;

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function isTimedPart(part) {
  return isFiniteNumber(part.start);
}

function seekToPart(part) {
  if (!isTimedPart(part)) return;

  mediaPlayerOpen = true;
  mediaTime = part.start;
  // Incrementing the id turns repeated clicks on the same timestamp into distinct seek events.
  seekRequestId += 1;
  seekRequest = { id: seekRequestId, time: part.start };
}

function handlePartKeydown(event, part) {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  event.preventDefault();
  seekToPart(part);
}

function handleMediaTimeChange(event) {
  mediaTime = event.detail.time;
}

function handleMediaVolumeChange(event) {
  mediaVolume = event.detail.volume;
  mediaMuted = event.detail.muted;
}

function handleMediaLayoutChange(event) {
  mediaPlayerPosition = event.detail.position;
  mediaPlayerSize = event.detail.size;
}

function openMediaPlayer() {
  seekRequest = null;
  mediaPlayerOpen = true;
}

function closeMediaPlayer() {
  seekRequest = null;
  mediaPlayerOpen = false;
}

function isActivePart(part, currentTime) {
  if (!isTimedPart(part) || !isFiniteNumber(currentTime)) return false;

  const end = isFiniteNumber(part.end) && part.end > part.start ? part.end : part.start + 0.5;
  return currentTime >= part.start && currentTime < end;
}

//load ui texts from ui Store
let uiTexts;
uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
});

$: mediaPlayerTexts = uiTexts?.transcription?.mediaPlayer ?? {};
</script>


<div
	class="transcript"
	use:copyWithDisclaimers={{
		before: transcriptionDisclaimerBefore,
		after: transcriptionDisclaimerAfter,
		excludeSelectors: ['.probability-score'],
		selectors: ['.original-text', '.translated-text'],
		disclaimersBySelector: [
			{
				selector: '.original-text',
				before: transcriptionDisclaimerBefore,
				after: transcriptionDisclaimerAfter,
			},
			{
				selector: '.translated-text',
				before: translationDisclaimerBefore,
				after: translationDisclaimerAfter,
			},
		],
	}}
>
  <div class="transcript-actions">
    <button type="button" class="secondary media-player-toggle" on:click={openMediaPlayer}>
      {mediaPlayerTexts.openMediaPlayer}
    </button>
    {#if hasSpeakers}
      <span class:save-error={correctionStatus === 'failed'} class="speaker-save-status">
        {correctionStatus === 'saving' ? 'Sprecherkorrekturen werden gespeichert …' : correctionStatus === 'failed' ? 'Speichern fehlgeschlagen' : correctionStatus === 'conflict' ? 'Sprecherkorrekturen wurden in einem anderen Tab geändert.' : 'Sprecherkorrekturen gespeichert'}
      </span>
      {#if correctionStatus === 'failed'}
        <button type="button" class="secondary" on:click={() => saveCorrections()}>Erneut versuchen</button>
      {/if}
      <button type="button" class="secondary" disabled={undoStack.length === 0 || correctionStatus === 'saving'} on:click={undoSpeakerChange}>Rückgängig</button>
      <button type="button" class="secondary" disabled={correctionStatus === 'saving'} on:click={resetSpeakerCorrections}>Sprecher zurücksetzen</button>
    {/if}
  </div>

  {#if dialogSpeakerId}
    <div class="speaker-editor" aria-label="Sprecher*in anpassen" style:top={`${editorPosition.top}px`} style:left={`${editorPosition.left}px`}>
      {#if dialogStep === 'rename'}
        <label for="speaker-name-editor">Neuer Name</label>
        <input
          id="speaker-name-editor"
          bind:this={editorInput}
          bind:value={editName}
          aria-label="Neuer Sprechername"
          aria-invalid={Boolean(speakerNameError)}
          aria-describedby={speakerNameError ? 'speaker-name-error' : undefined}
          on:input={handleSpeakerNameInput}
        />
        {#if speakerNameError}
          <span id="speaker-name-error" class="speaker-name-error">{speakerNameError}</span>
        {/if}
        <button type="button" disabled={Boolean(speakerNameError) || correctionStatus === 'saving'} on:click={confirmRename}>Speichern</button>
        {#if renameConflictSpeakerId}
          <span class="rename-conflict">Name bereits vorhanden.</span>
          <button type="button" class="speaker-editor-link" on:click={() => { mergeTarget = renameConflictSpeakerId; dialogStep = 'merge'; }}>Zusammenführen?</button>
        {:else}
          <button type="button" class="speaker-editor-link" on:click={() => dialogStep = 'merge'}>Zusammenführen</button>
        {/if}
      {:else}
        <select bind:value={mergeTarget} aria-label="Zielspeaker">
          <option value="">Sprecher*in wählen</option>
          {#each Object.entries(transcript.getSpeakerMap()) as [key, speaker]}
            {#if key !== dialogSpeakerId}<option value={key}>{speaker.name}</option>{/if}
          {/each}
        </select>
        <button type="button" disabled={!mergeTarget || correctionStatus === 'saving'} on:click={confirmMerge}>Zusammenführen</button>
        <button type="button" class="speaker-editor-link" on:click={() => dialogStep = 'rename'}>Umbenennen</button>
      {/if}
      <button type="button" class="speaker-editor-link" on:click={closeSpeakerDialog}>Abbrechen</button>
    </div>
  {/if}

  {#if mediaPlayerOpen}
    <TranscriptMediaPlayer
      {job}
      {seekRequest}
      initialTime={mediaTime ?? 0}
      initialVolume={mediaVolume}
      initialMuted={mediaMuted}
      initialPosition={mediaPlayerPosition}
      initialSize={mediaPlayerSize}
      on:timechange={handleMediaTimeChange}
      on:volumechange={handleMediaVolumeChange}
      on:layoutchange={handleMediaLayoutChange}
      on:close={closeMediaPlayer}
    />
  {/if}

	{#if transcript.hasMatchedTerms() }
	<div class="grey-bg">
		<h3 class="transcript-section">{uiTexts.transcription.foundKeywords}</h3>
		<div class="chips speaker-chips">
			{#each Object.entries(transcript.getMatchedTerms()) as [kategorie, begriffe]}
				<strong>{kategorie}: </strong>
					{#each begriffe as begriff}
						<div class="chip-item">
							{begriff.Fachbegriff}
							<span
								class="probability-score">{begriff.Bedeutung} </span>
							&nbsp;({begriff.count})
						</div>
					{/each}
			{/each}
		</div>
	</div>
	{/if}
	{#if hasSpeakers}
		<div class="grey-bg">
			<h3 class="transcript-section">{uiTexts.transcription.speakers}</h3>
			<div class="chips speaker-list">
				{#each Object.entries(transcript.getSpeakerMap()) as [key, speaker]}
					<div class="chip-item">
						<button type="button" class="speaker-chip-button" title="Sprecher*in anpassen" disabled={correctionStatus === 'saving'} on:click={(event) => openSpeakerDialog(key, event)}>{speaker.name}</button>
					</div>
				{/each}
			</div>
		</div>
	{/if}
	<div class="row">
		<div class="original-text">
			<h3>{uiTexts.transcription.transcriptedText}</h3>
      {#if transcriptionDisclaimerBefore}
				<div class="transcript-disclaimer">
					{transcriptionDisclaimerBefore}
				</div>
			{/if}
		</div>
		<!-- if translation; zeige translation -->
        {#if translationRows.length > 0}
		    <div class="translated-text">
					<h3>{uiTexts.transcription.translatedText}</h3>
					{#if translationDisclaimerBefore}
						<div class="transcript-disclaimer">
							{translationDisclaimerBefore}
						</div>
					{/if}
		    </div>
        {/if}
		<!-- /if Translation -->
	</div>
	{#each transcriptRows as row, index}
		<div class="row speaker-row">
			<div class="original-text">
				<div class="speaker">
					{#if row.speakerId || row.timeStamp}
						<p class="timeslot">
							{#if row.speakerId}
								<button type="button" class="speaker-label" title="Sprecher*in anpassen" disabled={correctionStatus === 'saving'} data-speakerID={row.speakerId} on:click={(event) => openSpeakerDialog(row.speakerId, event)}>{getSpeakerDisplayName(row.speakerId)}</button>
							{/if}
							{#if row.timeStamp}
								{#if row.speakerId}&nbsp;{/if}
								[<span>{row.timeStamp}</span>]
							{/if}
						</p>
					{/if}
					<p class="text">
						{#each row.parts as part, partIndex}
							<span
								data-score={part.score}
								class="abschnitt media-subsegment"
								class:active-subsegment={isActivePart(part, mediaTime)}
								class:seekable-subsegment={isTimedPart(part)}
								role="button"
								tabindex="0"
								on:click={() => seekToPart(part)}
								on:keydown={(event) => handlePartKeydown(event, part)}
							>
								{part.text}<span class="probability-score">Score: {formatScore(part.score)}</span>
							</span>
							{#if partIndex < row.parts.length - 1}{' '}{/if}
						{/each}
					</p>
				</div>
			</div>
			{#if translationRows.length > 0}
				<div class="translated-text">
					<div class="speaker">
						{#if translationRows[index] && (translationRows[index].speakerId || translationRows[index].timeStamp)}
							<p class="timeslot">
								{#if translationRows[index].speakerId}
									<button type="button" class="speaker-label" title="Sprecher*in anpassen" disabled={correctionStatus === 'saving'} data-speakerID={translationRows[index].speakerId} on:click={(event) => openSpeakerDialog(translationRows[index].speakerId, event)}>{getSpeakerDisplayName(translationRows[index].speakerId)}</button>
								{/if}
								{#if translationRows[index].timeStamp}
									{#if translationRows[index].speakerId}&nbsp;{/if}
									[<span>{translationRows[index].timeStamp}</span>]
								{/if}
							</p>
						{/if}
						<p class="text">{translationRows[index]?.text ?? ''}</p>
					</div>
				</div>
			{/if}
		</div>
	{/each}
  {#if transcriptionDisclaimerAfter || (translationRows.length > 0 && translationDisclaimerAfter)}
    <div class="row disclaimer-row">
      <div class="original-text">
        {#if transcriptionDisclaimerAfter}
          <div class="transcript-disclaimer">
            {transcriptionDisclaimerAfter}
          </div>
        {/if}
      </div>
      {#if translationRows.length > 0}
        <div class="translated-text">
          {#if translationDisclaimerAfter}
            <div class="transcript-disclaimer">
              {translationDisclaimerAfter}
            </div>
          {/if}
        </div>
      {/if}
    </div>
  {/if}

    <div style="display: none; justify-content: flex-start; gap: 12px">
    <!-- <div style="display: flex; justify-content: flex-start; gap: 12px"> -->
		<Link to="/zusammenfassung" style="min-width: 0;">
			<button class="secondary" style="width: auto; white-space:nowrap"
			on:click={() => {
                console.log("Dummy Button; Funktionalität noch zu implementieren.")
                }
            }
			>In Zusammenfassung übertragen</button>
		</Link>

		<Link to="/recherche" style="min-width: 0;">
			<button class="secondary" style="width: auto; white-space:nowrap"
			on:click={() => {
                console.log("Dummy Button; Funktionalität noch zu implementieren.")
                }
            }
			>In Recherche übertragen</button>
		</Link>
    </div>
</div>

<style>
	.transcript-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}
	.speaker-save-status { font-size: 0.9rem; }
	.save-error { color: #b00020; }
	.speaker-label {
		border: 0;
		background: none;
		padding: 0;
		margin: 0;
		display: inline;
		color: #000;
		font: inherit;
		font-weight: inherit;
		line-height: inherit;
		text-transform: none;
		text-decoration: underline;
		cursor: pointer;
	}
	.speaker-editor {
		position: fixed;
		z-index: 1000;
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
		width: min(340px, calc(100vw - 16px));
		margin: 0;
		padding: 12px;
		background: #fff;
		border: 1px solid #000;
		box-shadow: 0 4px 16px rgb(0 0 0 / 20%);
	}
	.speaker-editor label { flex-basis: 100%; font-size: 0.9rem; font-weight: bold; }
	.speaker-editor input, .speaker-editor select {
		width: auto;
		margin: 0;
		border: 1px solid #000;
		background: #fff;
		color: #000;
	}
	.speaker-editor-link {
		padding: 0;
		margin: 0;
		background: none;
		color: #000;
		font-size: 0.9rem;
		font-weight: normal;
		text-decoration: underline;
		text-transform: none;
	}
	.rename-conflict { color: #b00020; font-size: 0.9rem; }
	.speaker-name-error { flex-basis: 100%; color: #b00020; font-size: 0.9rem; }
	.transcript{
		padding-top: 12px;
	}
	.row{
		display: flex;
		gap: 24px;
	}
	.speaker-row{
		align-items: stretch;
	}
	.disclaimer-row{
		align-items: flex-start;
	}
	.original-text,
	.translated-text{
		flex: 1;
		padding-top: 8px;
		text-align: left;
	}

	.original-text h3,
	.translated-text h3{
		margin-bottom: 12px;
		font-size: 1rem;
		border-bottom: 1px solid #e2e2e2;
	}
	.transcript-disclaimer{
		margin-bottom: 12px;
		color: #666;
		font-size: 0.9rem;
		font-style: italic;
		line-height: 1.5;
	}
	.chip-item{
		position: relative;
	}
	.chip-item,
	.chips {
		display: flex;
		font-size: 0.85rem;
	}
	.chips strong{
		margin-right: 8px;
		padding-left: 16px;
	}
	.chips strong:first-child{
		padding-left: 0;
	}
	.grey-bg{
		background-color: var(--bg-color);
		padding: 12px;
		margin-bottom: 8px;
	}
	.grey-bg h3{
		font-size: .8em;
		margin: 0 0 12px;
		text-align: left;
		text-transform: none;
	}
	.speaker{
		text-align: left;
	}
	.speaker-row .speaker{
		display: flex;
		flex-direction: column;
		height: 100%;
	}
	.speaker p{
		margin: 0 0 12px;
		padding: 0;
	}
	.speaker-row .text{
		flex: 1;
		font-size: 1rem;
		font-weight: 500;
	}
	.speaker-row .timeslot{
		font-size: 1rem;
		font-weight: 700;
	}
	.speaker p.text > .abschnitt{
		position: relative;
	}
	.speaker p.text > .abschnitt:hover{
		background-color: #f2f2f2;
	}
	.probability-score{
		position: absolute;
		top:-2.5rem;
		left: 0;
		-webkit-user-select: none;
		user-select: none;
		background-color: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 6px rgba(0,0,0,0.3);
		padding: 2px 8px;
		border: 2px solid #f2f2f2;
		/*display: none;*/
		visibility: hidden;
		min-width: 90px;
		opacity: 0;
		transition: opacity 0.125s ease 0.25s,
				visibility 0s linear 0.25s;
	}
	.chips .chip-item:hover .probability-score,
	.speaker p.text > .abschnitt:hover .probability-score{
		/*display: block;*/
		opacity: 1;
		visibility: visible;
	}
	p.timeslot{
		margin: 0;
	}
	.chip-item button{
		padding:2px;
		margin-left: 0.5rem;
		background-color: transparent;
		border-radius: 25%;
	}
	.chip-item {
		padding:2px 8px;
		margin-right: 8px;
		border-radius: 20px;
		border: #2c2929 1px solid;
		font-size: 0.85rem;
	}
	.speaker-chips{
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}
	.speaker-list {
		flex-wrap: wrap;
		gap: 8px;
	}
	.speaker-chips .chip-item{
		flex: 0 0 240px;	/* change these */
		max-width: 240px;	/* to adjust speaker rename width */
		margin-right: 0;
		display: flex;
		align-items: center;
		box-sizing: border-box;
	}
	.speaker-chips .chip-item button{
		flex: 0 0 auto;
	}
	.speaker-list .chip-item {
		max-width: min(100%, 240px);
		margin: 0;
	}
	.speaker-chip-button {
		max-width: 100%;
		padding: 2px 4px;
		margin: 0;
		color: #000;
		background: transparent;
		font-size: inherit;
		font-weight: inherit;
		line-height: inherit;
		text-align: left;
		text-transform: none;
		white-space: normal;
		overflow-wrap: anywhere;
	}

	.transcript-actions{
		display: flex;
		justify-content: flex-start;
		margin-bottom: 12px;
	}
	.media-player-toggle{
		width: auto;
		margin: 0;
	}
	.media-subsegment{
		display: inline;
		color: inherit;
		font: inherit;
		font-weight: inherit;
		line-height: inherit;
		letter-spacing: inherit;
		text-align: inherit;
		text-transform: none;
		white-space: normal;
		background: transparent;
		border-radius: 2px;
		box-decoration-break: clone;
		-webkit-box-decoration-break: clone;
	}
	.media-subsegment.seekable-subsegment{
		cursor: pointer;
	}
	.media-subsegment:not(.seekable-subsegment){
		cursor: default;
	}
	.speaker p.text > .media-subsegment.active-subsegment,
	.speaker p.text > .media-subsegment.active-subsegment:hover{
		background-color: rgba(255, 230, 0, 0.65);
	}
	.media-subsegment:focus-visible{
		outline: 2px solid #000;
		outline-offset: 2px;
	}

</style>
